/** A path prefix that has nodes beneath it. */
export interface PathTreeGroup<TLeaf> {
  kind: "group";
  /** `treeGroupId(path)`, so a group never shares a leaf's id. */
  id: string;
  /** The prefix's segments joined with `.`. */
  path: string;
  /** The prefix's last segment. */
  label: string;
  /** 0-based level. */
  depth: number;
  /** Every leaf anywhere beneath the group. */
  leaves: readonly TLeaf[];
}

/** One of the leaves the tree was built from. */
export interface PathTreeLeaf<TLeaf> {
  kind: "leaf";
  /** `getId(leaf)`. */
  id: string;
  /** The leaf's segments joined with `.`. */
  path: string;
  /** The leaf's last segment. */
  label: string;
  /** 0-based level. */
  depth: number;
  leaf: TLeaf;
}

export type PathTreeNode<TLeaf> = PathTreeGroup<TLeaf> | PathTreeLeaf<TLeaf>;

/**
 * A tree of leaves by their paths, read by id: the shape a tree view's data
 * loader asks for, and what a tree table flattens into rows.
 */
export interface PathTree<TLeaf> {
  /** The id of the unnamed root; its children are the top level. */
  rootId: string;
  /** The node with `id`, or undefined for an id the tree does not hold. */
  getNode: (id: string) => PathTreeNode<TLeaf> | undefined;
  /** The ids of the nodes directly under `id`, in order. */
  getChildren: (id: string) => string[];
  /** Whether any node has children; with none, the tree is a flat list. */
  hasGroups: boolean;
}

export interface BuildPathTreeParams<TLeaf> {
  leaves: readonly TLeaf[];
  /** The leaf's place in the tree, one segment per level: `["imu", "accel", "x"]`. */
  getPath: (leaf: TLeaf) => readonly string[];
  getId: (leaf: TLeaf) => string;
}

export function treeGroupId(path: string): string {
  return `group:${path}`;
}

/**
 * Every dotted prefix of `path`, outermost first, without `path` itself: the
 * groups to open to show what sits at `path`. `"imu.accel.x"` gives
 * `["imu", "imu.accel"]`; an undotted path gives none.
 */
export function ancestorPaths(path: string): string[] {
  const segments = path.split(".");
  return segments
    .slice(1)
    .map((_, index) => segments.slice(0, index + 1).join("."));
}

const ROOT_ID = "";

const compareLabels = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

interface BuildNode<TLeaf> {
  label: string;
  path: string;
  children: Map<string, BuildNode<TLeaf>>;
  /** Leaves whose path ends at this node. */
  leaves: TLeaf[];
}

/**
 * Turns leaves with paths into a tree: a group for each shared path prefix,
 * one level per segment, with single-child chains kept rather than merged.
 * Each level lists its groups and leaves together A to Z, numbers by value. A
 * leaf whose path is also a group's (`imu` beside `imu.x`) is listed inside
 * that group, first among equals.
 */
export function buildPathTree<TLeaf>({
  leaves,
  getPath,
  getId,
}: BuildPathTreeParams<TLeaf>): PathTree<TLeaf> {
  const root: BuildNode<TLeaf> = {
    label: "",
    path: "",
    children: new Map(),
    leaves: [],
  };
  for (const leaf of leaves) {
    let node = root;
    for (const segment of getPath(leaf)) {
      let child = node.children.get(segment);
      if (!child) {
        child = {
          label: segment,
          path: node === root ? segment : `${node.path}.${segment}`,
          children: new Map(),
          leaves: [],
        };
        node.children.set(segment, child);
      }
      node = child;
    }
    node.leaves.push(leaf);
  }

  const nodes = new Map<string, PathTreeNode<TLeaf>>();
  const childIds = new Map<string, string[]>();

  /** Adds the nodes under `node` at `depth`, returning every leaf beneath it. */
  const add = (node: BuildNode<TLeaf>, id: string, depth: number): TLeaf[] => {
    const items: { label: string; add: () => string }[] = [];
    const leafItem = (leaf: TLeaf, path: string, label: string) => ({
      label,
      add: () => {
        const leafId = getId(leaf);
        nodes.set(leafId, {
          kind: "leaf",
          id: leafId,
          path,
          label,
          depth,
          leaf,
        });
        return leafId;
      },
    });
    // A node that is both a leaf and a group lists its own leaves among its
    // children, under its own label.
    if (node !== root && node.children.size > 0) {
      for (const leaf of node.leaves) {
        items.push(leafItem(leaf, node.path, node.label));
      }
    }
    const beneath: TLeaf[] = [...(node === root ? [] : node.leaves)];
    for (const child of node.children.values()) {
      if (child.children.size === 0) {
        for (const leaf of child.leaves) {
          items.push(leafItem(leaf, child.path, child.label));
          beneath.push(leaf);
        }
        continue;
      }
      items.push({
        label: child.label,
        add: () => {
          const groupId = treeGroupId(child.path);
          const groupLeaves = add(child, groupId, depth + 1);
          nodes.set(groupId, {
            kind: "group",
            id: groupId,
            path: child.path,
            label: child.label,
            depth,
            leaves: groupLeaves,
          });
          beneath.push(...groupLeaves);
          return groupId;
        },
      });
    }
    items.sort((a, b) => compareLabels(a.label, b.label));
    childIds.set(
      id,
      items.map((item) => item.add()),
    );
    return beneath;
  };
  add(root, ROOT_ID, 0);

  return {
    rootId: ROOT_ID,
    getNode: (id) => nodes.get(id),
    getChildren: (id) => childIds.get(id) ?? [],
    hasGroups: [...nodes.values()].some((node) => node.kind === "group"),
  };
}
